import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button, Champ, Etiquette } from "@/shared/ui";
import { ArrowLeft, Loader2, Mail } from "@/shared/ui/icons";

/**
 * Se connecter SANS la page WorkOS — pour cyrano.work (Kayla, 13-10-2026 :
 * « dans la page de connexion, il y a toujours le logo du cockpit »).
 *
 * La page hébergée par WorkOS (AuthKit) porte la marque de TOUT l'environnement
 * WorkOS — celle de Dozka : elle ne peut pas changer selon le domaine. On ne
 * l'ouvre donc jamais :
 *
 *   · Google / Apple / Microsoft → `/api/auth/login?provider=…`, qui va droit
 *     chez le fournisseur (Cyrano tient la liste fermée, `FOURNISSEURS_DIRECTS`) ;
 *   · e-mail → un code à usage unique (`/api/auth/code`, puis
 *     `/api/auth/code/verifier`, qui pose le cookie de session).
 *
 * Mêmes routes que l'écran de app.rishfans.com (`rishfans/ConnexionRishfans`),
 * mais aux couleurs du thème du cockpit : c'est `LoginScreen` qui l'encadre.
 */

type Fournisseur = "GoogleOAuth" | "AppleOAuth" | "MicrosoftOAuth";

/** Où revenir après la connexion : la page demandée, jamais une autre origine
 *  (même règle que `useSession.signIn`, que le backend re-valide). */
export function retourApresConnexion(chemin: string): string {
  return chemin.startsWith("/") && !chemin.startsWith("//") ? chemin : "/";
}

function cheminActuel(): string {
  return retourApresConnexion(window.location.pathname + window.location.search);
}

async function appeler(chemin: string, corps: Record<string, string>): Promise<boolean> {
  try {
    const r = await fetch(chemin, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(corps),
    });
    return r.ok;
  } catch {
    return false;
  }
}

export function ConnexionDirecte() {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [etape, setEtape] = useState<"email" | "code">("email");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  function seConnecterAvec(fournisseur: Fournisseur) {
    window.location.href = `/api/auth/login?provider=${fournisseur}&return_to=${encodeURIComponent(cheminActuel())}`;
  }

  async function envoyerLeCode(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || enCours) return;
    setEnCours(true);
    setErreur(null);
    const ok = await appeler("/api/auth/code", { email: email.trim() });
    setEnCours(false);
    if (ok) {
      setEtape("code");
      setCode("");
    } else {
      setErreur(t("auth.direct.erreurEnvoi"));
    }
  }

  async function verifierLeCode(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || enCours) return;
    setEnCours(true);
    setErreur(null);
    const ok = await appeler("/api/auth/code/verifier", { email: email.trim(), code: code.trim() });
    if (ok) {
      // La session est posée (cookie) : on recharge la page demandée.
      window.location.href = cheminActuel();
      return;
    }
    setEnCours(false);
    setErreur(t("auth.direct.erreurCode"));
  }

  return (
    <div>
      {etape === "email" ? (
        <>
          <div className="flex flex-col gap-2.5">
            <BoutonFournisseur onClick={() => seConnecterAvec("GoogleOAuth")} icone={<IconeGoogle />}>
              {t("auth.direct.google")}
            </BoutonFournisseur>
            <BoutonFournisseur onClick={() => seConnecterAvec("AppleOAuth")} icone={<IconeApple />}>
              {t("auth.direct.apple")}
            </BoutonFournisseur>
            <BoutonFournisseur onClick={() => seConnecterAvec("MicrosoftOAuth")} icone={<IconeMicrosoft />}>
              {t("auth.direct.microsoft")}
            </BoutonFournisseur>
          </div>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-fg/10" />
            <Etiquette>{t("auth.direct.ou")}</Etiquette>
            <span className="h-px flex-1 bg-fg/10" />
          </div>

          <form onSubmit={envoyerLeCode} className="flex flex-col gap-3">
            <Champ
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.direct.emailPlaceholder")}
              icone={<Mail />}
              taille="lg"
            />
            <Button type="submit" variant="accent" size="xl" disabled={enCours || !email.trim()} className="w-full">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.direct.recevoirCode")}
            </Button>
          </form>
        </>
      ) : (
        <form onSubmit={verifierLeCode} className="flex flex-col gap-3">
          <Button
            type="button"
            variant="ghost"
            size="md"
            icon={<ArrowLeft className="h-3.5 w-3.5" />}
            onClick={() => {
              setEtape("email");
              setErreur(null);
            }}
            className="self-start"
          >
            {t("auth.direct.retour")}
          </Button>
          <p className="text-[14px] leading-relaxed text-muted">
            {t("auth.direct.codeEnvoye")} <strong className="font-semibold text-fg">{email.trim()}</strong>
          </p>
          <Champ
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
            placeholder="123456"
            maxLength={12}
            taille="lg"
            style={{ textAlign: "center", letterSpacing: "0.4em", fontSize: 20 }}
          />
          <Button type="submit" variant="accent" size="xl" disabled={enCours || !code.trim()} className="w-full">
            {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.signIn")}
          </Button>
        </form>
      )}

      {erreur ? (
        <p role="alert" className="mt-4 text-center text-[13px] text-erreur">
          {erreur}
        </p>
      ) : null}
    </div>
  );
}

function BoutonFournisseur({ onClick, icone, children }: { onClick: () => void; icone: ReactNode; children: ReactNode }) {
  return (
    <Button type="button" variant="secondary" size="xl" onClick={onClick} icon={icone} className="w-full">
      {children}
    </Button>
  );
}

function IconeGoogle() {
  return (
    <svg viewBox="0 0 48 48" className="h-[18px] w-[18px]" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function IconeApple() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden>
      <path d="M16.37 12.6c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.82-.81-3-.79-1.54.02-2.96.9-3.76 2.27-1.6 2.78-.41 6.9 1.15 9.16.76 1.1 1.67 2.34 2.86 2.3 1.15-.05 1.58-.74 2.97-.74 1.38 0 1.77.74 2.98.72 1.23-.02 2.01-1.12 2.76-2.23.87-1.28 1.23-2.52 1.25-2.58-.03-.01-2.4-.92-2.42-3.65zM14.1 5.84c.63-.77 1.06-1.83.94-2.89-.91.04-2.01.61-2.66 1.37-.58.67-1.09 1.75-.96 2.79 1.02.08 2.05-.52 2.68-1.27z" />
    </svg>
  );
}

function IconeMicrosoft() {
  return (
    <svg viewBox="0 0 24 24" className="h-[16px] w-[16px]" aria-hidden>
      <path fill="#F25022" d="M1 1h10.5v10.5H1z" />
      <path fill="#7FBA00" d="M12.5 1H23v10.5H12.5z" />
      <path fill="#00A4EF" d="M1 12.5h10.5V23H1z" />
      <path fill="#FFB900" d="M12.5 12.5H23V23H12.5z" />
    </svg>
  );
}
